import { ErrorLine } from "../components/ui";
import { useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase";
import { Logo } from "../components/Logo";
import { Lnk } from "../components/Lnk";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    // A LOGIN SWITCHED OFF (0357) -- archived from every campaign -- comes back from Supabase as
    // "User is banned", which reads like an accusation and says nothing about what to do.
    if (error) setErr(/banned/i.test(error.message)
      ? "This login is switched off because it isn't on any campaign right now. If that's a mistake, ask your campaign manager."
      : error.message);
    setBusy(false);
  }

  async function forgot() {
    // Explicit reset-email field, not the login email — you choose exactly which inbox gets the link.
    const target = resetEmail.trim();
    if (!target) { setErr("Enter the email to send the reset link to."); return; }
    setErr(null); setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(target, {
      // The account page is what implements set-password, and it is on the redirect allow-list;
      // this origin is not. Sending someone back here gives them a dead link.
      redirectTo: (import.meta.env.VITE_ACCOUNT_URL as string) || "https://account.oratorcrm.com",
    });
    setBusy(false);
    if (error) setErr(error.message); else setSent(true);
  }

  return (
    <div className="min-h-screen grid place-items-center bg-bg">
      <form onSubmit={submit}
        className="bg-surface border border-border rounded-xl2 shadow-sm w-[360px] p-6 flex flex-col gap-3">
        <div className="flex items-center gap-2.5 font-extrabold text-lg tracking-tight mb-1"><Logo /> Orator</div>
        <p className="text-muted text-sm mt-0 mb-2">Sign in to your campaign workspace.</p>
        <label className="text-xs font-bold uppercase tracking-wide text-muted">Email
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-text outline-none" />
        </label>
        <label className="text-xs font-bold uppercase tracking-wide text-muted">Password
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-text outline-none" />
        </label>
        <ErrorLine message={err} />
        {sent && <div className="text-good text-sm font-semibold">Reset link sent to {resetEmail.trim()}. Check that inbox.</div>}
        <button type="submit" disabled={busy}
          className="mt-1 bg-accent text-white rounded-lg px-3.5 py-2.5 font-bold disabled:opacity-60">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {!resetOpen ? (
          <Lnk onClick={() => { setResetOpen(true); setResetEmail((r) => r || email); setErr(null); setSent(false); }} disabled={busy} className="text-sm font-semibold self-center">Forgot password?</Lnk>
        ) : (
          <div className="rounded-lg border border-border bg-surface-2 p-3 flex flex-col gap-2">
            <div className="text-xs font-bold uppercase tracking-wide text-muted">Send a reset link to</div>
            <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); forgot(); } }} placeholder="name@example.com" className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-text outline-none" autoFocus />
            <div className="flex gap-2 items-center">
              <button type="button" onClick={forgot} disabled={busy} className="bg-accent text-white rounded-lg px-3 py-2 font-bold text-sm disabled:opacity-60 flex-1">{busy ? "Sending…" : "Send reset link"}</button>
              <button type="button" onClick={() => { setResetOpen(false); setSent(false); setErr(null); }} className="text-sm px-2 text-text font-semibold underline underline-offset-2">Cancel</button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
